import { ApiError, type ApiOpts } from '@/lib/api';
import type { StudyAnalysis, StudyDocumentPageImage } from '@/pages/learn/study/studyAnalysisApi';
import type { StudyDocumentDetail } from '@/pages/learn/study/studyApi';
import type { StudyNote, StudyNoteVersion, NoteWrite } from '@/pages/learn/study/studyNotesApi';
import type {
  StudyPracticeSet,
  StudyAnswerCheck,
  StudyAnalysisReviewItem,
} from '@/pages/learn/study/studyAnalysisFollowUpApi';
import type { NoteOperation } from '@/pages/learn/study/studyNotebookOperationsApi';
import type { WorkingRow } from '@/pages/learn/study/workingCanvas';
import type { MaterialViewState } from '@/pages/learn/study/useMaterialViewState';
import {
  exampleMaterial,
  exampleSource,
  exampleNotes,
  examplePoints,
  examplePractice,
} from '@/pages/learn/study/studyMaterialLearningExampleData';
import { SAMPLE_IDS, sampleAnalysisPath, sampleExplanationPath } from './sampleProfile';
import { sampleExplanation } from './sampleExplanation';
import { samplePdfUrl, samplePageImage, samplePageLayout } from './sampleSourceAssets';

const fields = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const copy = <T>(value: T): T => structuredClone(value);
const fail = (code: string, message: string): never => {
  throw new ApiError(409, code, message);
};
const noAi = () =>
  fail(
    'STUDY_EXPERIENCE_ONLY',
    'This sample contains prepared results. Live AI, audio generation and Stars are unavailable; no request was sent.',
  );
type SavedWorking = WorkingRow & { updated_at: string };

/** Local state behind the existing public-demo seam. It never fetches or fabricates a cloud receipt. */
export function createSampleMaterialExperience(context: {
  documents: StudyDocumentDetail[];
  notes: StudyNote[];
  working: Map<string, SavedWorking>;
  subject: { subject_key: string; display_name: string };
  termId: string;
  date: string;
}) {
  const { documents, notes, working, subject, termId, date } = context;
  const stamp = `${date}T02:00:00.000Z`;
  const earlier = new Date(Date.parse(stamp) - 3 * 86400000).toISOString();
  const pdf = samplePdfUrl();
  const document: StudyDocumentDetail = {
    id: SAMPLE_IDS.document,
    file_name: 'Alex-fractions-sample.pdf',
    display_name: exampleMaterial.title,
    mime_type: 'application/pdf',
    size_bytes: pdf.bytes,
    status: 'ready',
    classification_state: 'learner_confirmed',
    term_id: termId,
    subject,
    created_at: earlier,
    read_url: pdf.url,
    read_url_expires_in: 3600,
    source_revision: 'fictional-prepared-source-v1',
    thumbnail: { kind: 'pdf', url: pdf.url, expires_in: 3600 },
    analysis: {
      status: 'succeeded',
      topic: exampleMaterial.title,
      questions_total: 1,
      questions_done: 0,
    },
  };
  documents.push(document);
  const note: StudyNote = {
    id: SAMPLE_IDS.note,
    title: 'Fractions — Alex’s learning notes',
    body: exampleNotes.chapters
      .map((chapter) => `${chapter.title}\n\n${chapter.body}`)
      .join('\n\n'),
    topic: 'Fractions',
    subject_key: 'mathematics',
    term_id: termId,
    class_date: date,
    pages: [{ document_id: document.id, rotation: 0 }],
    version: 1,
    status: 'active',
    available: true,
    updated_at: earlier,
    linked_task_ids: ['example-task'],
  };
  notes.unshift(note);
  const versions: StudyNoteVersion[] = [];
  const addVersion = () =>
    versions.unshift({
      version: note.version,
      provenance: note.version === 1 ? 'ai_organised' : 'learner_authored',
      created_at: new Date().toISOString(),
      available: true,
      content: {
        ...copy(note),
        request_id: `sample-version-${note.version}`,
        expected_version: note.version - 1,
        status: 'active',
      } as NoteWrite,
    });
  addVersion();
  const analysis: StudyAnalysis = {
    id: SAMPLE_IDS.analysis,
    status: 'succeeded',
    version: 1,
    parent_analysis_id: null,
    primary_document_id: document.id,
    document_ids: [document.id],
    prompt_version: 'prepared-fictional-example-v1',
    notes_depth: 'in_depth',
    delivery_stage: 'ready',
    model_ids: [],
    progress: {
      pages_total: 3,
      pages_read: 3,
      pages_failed: 0,
      questions_total: 1,
      questions_done: 0,
    },
    stars: { per_page: 0, quoted: 0, charged: 0, refunded: 0 },
    refund: { state: 'none', owed_stars: 0 },
    error_code: null,
    created_at: earlier,
    completed_at: earlier,
    pages: [0, 1, 2].map((index) => ({
      page_index: index,
      document_id: document.id,
      page_number: index + 1,
      status: 'read',
      error_code: null,
      legible: 'good',
      handwriting: 'none',
      blocks: samplePageLayout(index).map((block) => ({
        id: block.id,
        kind: block.id === 'b3' ? 'question' : 'text',
        text: block.text,
        region: block.region,
        confidence: 'high',
        provenance: 'ai_transcription',
      })),
    })),
    result: {
      learning_note_id: note.id,
      learning_notes: copy(exampleNotes),
      material_type: 'homework',
      subject: { key: 'mathematics', label: 'Mathematics', confidence: 'high' },
      topic: { label: 'Fractions', confidence: 'high' },
      year_level_guess: { value: null, confidence: 'low' },
      questions: [
        {
          id: 'example-pizza-source',
          number: '1',
          text: exampleSource[2].text,
          page_index: 0,
          region: samplePageLayout(0).find((block) => block.id === 'b3')!.region,
          kind: 'working',
          marks: null,
          marked_wrong: null,
          source: 'ai',
          status: 'not_started',
          study_session_id: null,
          text_edited: false,
        },
      ],
      key_points: examplePoints.map((point) => ({
        text: point.content.name,
        source: 'from_page',
        block_ids: point.content.citations.map((ref) => ref.block_id),
      })),
      key_terms: [
        {
          term: 'Denominator',
          meaning: 'The number of equal groups in one whole.',
          source: 'from_page',
        },
      ],
      formulas: [],
      dates: [],
      warnings: [],
      rubric: null,
    },
    learner_confirmed: { subject: true, topic: true, material_type: true },
    edits: [],
    learner_seen_at: earlier,
  };
  const history: Array<{
    kind: string;
    id: string;
    title: string;
    at: string;
    href: string;
    evidence: string;
  }> = [
    {
      kind: 'material',
      id: document.id,
      title: 'Saved the three-page fractions worksheet',
      at: earlier,
      href: `/learn/study/materials/${document.id}`,
      evidence: 'Prepared fictional history · source pages retained',
    },
    {
      kind: 'note',
      id: note.id,
      title: 'Prepared notes and three source-linked learning points',
      at: earlier,
      href: sampleAnalysisPath,
      evidence: 'Prepared fictional history · not a real AI receipt',
    },
  ];
  const rectangles = Array.from({ length: 4 }, (_, group) => {
    const x = 80 + group * 170;
    return [
      [
        [x, 350],
        [x + 160, 350],
        [x + 160, 440],
        [x, 440],
        [x, 350],
      ],
      [
        [x + 80, 350],
        [x + 80, 440],
      ],
    ];
  }).flat();
  const sampleStrokes = [
    ...rectangles,
    [
      [80, 465],
      [580, 465],
    ],
  ].map((pairs, index) => ({
    id: `bb000000-0000-4000-8000-${String(index + 1).padStart(12, '0')}`,
    colour: index === rectangles.length ? ('sky' as const) : ('ink' as const),
    pointer: 'pen' as const,
    width: 3,
    points: pairs.map(([x, y], point) => ({ x, y, pressure: 0.5, time: point * 20 })),
  }));
  working.set(SAMPLE_IDS.working, {
    id: SAMPLE_IDS.working,
    version: 1,
    locked: false,
    context: { kind: 'material', question_id: document.id },
    updated_at: earlier,
    content: {
      format_version: 1,
      height: 1000,
      background: 'grid',
      question: exampleSource[2].text,
      text: 'My first step: divide eight slices into four equal groups.\nOne quarter is 8 ÷ 4 = 2 slices.\nNext I need three groups, not three slices.',
      strokes: sampleStrokes,
    },
  });
  const attempts: StudyAnswerCheck[] = [
    {
      id: 'example-attempt-before-visit',
      question_id: examplePractice[0].id,
      practice_set_id: SAMPLE_IDS.set,
      status: 'checked',
      verdict: 'incorrect',
      hint: examplePractice[0].hint,
      student_answer_text: 'A',
      stars: 0,
      stars_refunded: 0,
      refunded: false,
      error_code: null,
      created_at: earlier,
      completed_at: earlier,
    },
  ];
  const reviews: StudyAnalysisReviewItem[] = [
    {
      kind: 'analysis',
      id: 'example-review-fractions',
      analysis_id: analysis.id,
      document_id: document.id,
      document_name: exampleMaterial.title,
      question_id: examplePractice[0].id,
      question_number: '1',
      practice_set_id: SAMPLE_IDS.set,
      topic: 'Equal parts of one whole',
      subject,
      source: 'practice_wrong',
      created_at: earlier,
      review_due_at: stamp,
      due: true,
    },
  ];
  const receipts = new Map<string, { fingerprint: string; value: unknown }>();
  let preparedExplanation: ReturnType<typeof sampleExplanation> | null = null;
  history.unshift({
    kind: 'session',
    id: SAMPLE_IDS.explanation,
    title: 'Read an explanation of equal groups',
    at: earlier,
    href: sampleExplanationPath,
    evidence: 'Prepared fictional explanation · no live AI call',
  });
  const points = copy(examplePoints);
  points[2].content.question_ids = ['example-pizza-source'];
  const operations: NoteOperation[] = [
    {
      id: 'example-operation-page-explanation',
      notebook_id: note.id,
      kind: 'region',
      status: 'ready',
      source_version: 1,
      stale: false,
      source: 'saved_notes',
      revision: 1,
      stars_charged: 0,
      stars_refunded: 0,
      error_code: null,
      result: {
        text: 'Pre-generated example explanation — not a live AI reply.\n\nIn the first-page pizza question, the denominator 4 means four equal groups of the whole. Eight slices make four groups of two. The numerator 3 selects three of those groups. Draw the groups and count their slices before you compare three quarters with one half. The Fractions Lab lets you change the number of equal parts and compare the same fraction as a decimal and a percentage. Return to this saved page explanation after exploring.',
      },
      practice_set_id: null,
      audio: null,
    },
    {
      id: 'example-operation-quiz',
      notebook_id: note.id,
      kind: 'quiz',
      status: 'ready',
      source_version: 1,
      stale: false,
      source: 'saved_notes',
      revision: 1,
      stars_charged: 0,
      stars_refunded: 0,
      error_code: null,
      result: { analysis_id: analysis.id },
      practice_set_id: SAMPLE_IDS.set,
      audio: null,
    },
    {
      id: 'example-operation-cards',
      notebook_id: note.id,
      kind: 'flashcards',
      status: 'ready',
      source_version: 1,
      stale: false,
      source: 'saved_notes',
      revision: 1,
      stars_charged: 0,
      stars_refunded: 0,
      error_code: null,
      result: {
        cards: [
          {
            front: 'What does the denominator tell you?',
            back: 'How many equal groups make one whole.',
            evidence: exampleSource[0].text,
          },
          {
            front: 'How do you find a/b of N?',
            back: 'Find one group with N ÷ b, then take a groups.',
            evidence: exampleSource[5].text,
          },
        ],
      },
      practice_set_id: null,
      audio: null,
    },
  ];
  let view = {
    version: 0,
    analysis_id: analysis.id,
    current_notebook_version: note.version,
    state: null as MaterialViewState | null,
    restore_notices: [] as string[],
  };
  const pages = new Map<number, Promise<StudyDocumentPageImage>>();
  const setView = (): StudyPracticeSet => ({
    id: SAMPLE_IDS.set,
    analysis_id: analysis.id,
    document_id: document.id,
    status: 'ready',
    question_kinds: ['multiple_choice'],
    stars_charged: 0,
    stars_refunded: 0,
    refunded: false,
    check_stars_per_attempt: 0,
    error_code: null,
    created_at: earlier,
    completed_at: earlier,
    questions: examplePractice.map((question) => {
      const own = attempts.filter((attempt) => attempt.question_id === question.id);
      return {
        id: question.id,
        stem: question.stem,
        kind: 'multiple_choice',
        options: question.options,
        attempts: own.length,
        answer_available: own.length > 0,
        answer: own.length
          ? {
              answer: question.options['ABCD'.indexOf(question.correct)],
              correct_option: question.correct as 'A' | 'B' | 'C' | 'D',
              worked_solution: question.feedback,
            }
          : null,
        latest_attempt: own.at(-1) ?? null,
        knowledge_ids: [question.knowledge_id],
        knowledge_notebook_id: note.id,
        knowledge_notebook_version: 1,
        knowledge_versions: { [question.knowledge_id]: 1 },
      };
    }),
  });
  const ownedNote = (id: string) => {
    if (id !== note.id) return false;
    return true;
  };
  return {
    reviews,
    history,
    note,
    analysis,
    async handle(path: string, options: ApiOpts): Promise<{ value: unknown } | undefined> {
      const url = new URL(path, 'https://sample.invalid'),
        p = url.pathname,
        method = options.method ?? 'GET',
        body = fields(options.body);
      const ok = (value: unknown) => ({ value });
      if (p === `/study/sessions/${SAMPLE_IDS.explanation}` && method === 'GET') {
        preparedExplanation ??= sampleExplanation(document, termId, earlier);
        return ok(await preparedExplanation);
      }
      if (p.startsWith(`/study/sessions/${SAMPLE_IDS.explanation}`)) return noAi();
      const page = new RegExp(`^/study/documents/${document.id}/pages/(\\d+)$`).exec(p);
      if (page && method === 'GET') {
        const number = Number(page[1]);
        if (number < 1 || number > 3) fail('EXAMPLE_NOT_FOUND', 'This sample has three pages.');
        if (!pages.has(number))
          pages.set(
            number,
            samplePageImage(number - 1).then((image) => ({
              ...image,
              document_id: document.id,
              page_number: number,
              provenance: 'client_pdfjs',
              read_url_expires_in: 3600,
            })),
          );
        return ok(await pages.get(number));
      }
      if (p === `/study/documents/${document.id}/analysis` && method === 'GET') return ok(analysis);
      if (p === `/study/analyses/${analysis.id}` && method === 'GET') return ok(analysis);
      if (p === `/study/analyses/${analysis.id}/seen` && method === 'POST')
        return ok({ analysis_id: analysis.id, status: 'succeeded', learner_seen_at: stamp });
      if (p === `/study/analyses/${analysis.id}/answer-checks/latest` && method === 'GET')
        return ok({ analysis_id: analysis.id, items: [] });
      if (p === '/study/review' && method === 'GET') return ok(reviews);
      const done = /^\/study\/review\/analysis-items\/([^/]+)\/done$/.exec(p);
      if (done && method === 'POST') {
        const index = reviews.findIndex((review) => review.id === done[1]);
        if (index < 0) fail('EXAMPLE_NOT_FOUND', 'This sample review is unavailable.');
        reviews.splice(index, 1);
        history.unshift({
          kind: 'review',
          id: done[1],
          title: 'Revisited the fractions idea',
          at: new Date().toISOString(),
          href: sampleAnalysisPath,
          evidence: 'Your action in this local demo · not mastery',
        });
        return ok({ id: done[1], reviewed_at: new Date().toISOString() });
      }
      const scope = new RegExp(`^/study/analyses/${analysis.id}/practice-sets(?:/(.*))?$`).exec(p);
      if (scope) {
        if (!scope[1] && method === 'GET') {
          const requested = url.searchParams.get('set_id');
          if (requested && requested !== SAMPLE_IDS.set)
            fail('EXAMPLE_NOT_FOUND', 'This is not a sample practice set.');
          return ok({ document_id: document.id, items: [setView()] });
        }
        const attempt = new RegExp(`^${SAMPLE_IDS.set}/questions/(.+)/attempts$`).exec(
          scope[1] ?? '',
        );
        if (attempt && method === 'POST') {
          const question = examplePractice.find(
            (item) => item.id === decodeURIComponent(attempt[1]),
          );
          if (!question) fail('EXAMPLE_NOT_FOUND', 'This question is not in the sample.');
          const choice = String(body.choice);
          if (!['A', 'B', 'C', 'D'].includes(choice) || body.expected_stars !== 0) noAi();
          const receiptKey = `attempt:${question!.id}:${String(body.idempotency_key)}`;
          const prior = receipts.get(receiptKey);
          if (prior) {
            if (prior.fingerprint !== choice)
              fail(
                'STUDY_ANSWER_CHECK_KEY_CHANGED',
                'Use a new local attempt for a changed answer.',
              );
            return ok(prior.value);
          }
          const correct = choice === question!.correct,
            now = new Date().toISOString();
          const check: StudyAnswerCheck = {
            id: `example-attempt-${attempts.length + 1}`,
            question_id: question!.id,
            practice_set_id: SAMPLE_IDS.set,
            status: 'checked',
            verdict: correct ? 'correct' : 'incorrect',
            hint: correct ? null : question!.hint,
            student_answer_text: choice,
            stars: 0,
            stars_refunded: 0,
            refunded: false,
            error_code: null,
            created_at: now,
            completed_at: now,
          };
          attempts.push(check);
          if (!correct && !reviews.some((review) => review.question_id === question!.id))
            reviews.push({
              kind: 'analysis',
              id: `example-review-${attempts.length}`,
              analysis_id: analysis.id,
              document_id: document.id,
              document_name: exampleMaterial.title,
              question_id: question!.id,
              question_number: String(examplePractice.indexOf(question!) + 1),
              practice_set_id: SAMPLE_IDS.set,
              topic: points.find((point) => point.id === question!.knowledge_id)!.content.name,
              subject,
              source: 'practice_wrong',
              created_at: now,
              review_due_at: now,
              due: true,
            });
          history.unshift({
            kind: 'practice',
            id: check.id,
            title: `Tried fractions question ${examplePractice.indexOf(question!) + 1}`,
            at: now,
            href: `${sampleAnalysisPath}&practiceSet=${SAMPLE_IDS.set}`,
            evidence: `Your local demo answer · ${correct ? 'matched prepared answer' : 'revisit the related idea'}`,
          });
          const value = {
            practice_set_id: SAMPLE_IDS.set,
            attempt: check,
            question: setView().questions.find((item) => item.id === question!.id)!,
          };
          receipts.set(receiptKey, { fingerprint: choice, value });
          return ok(value);
        }
        return noAi();
      }
      if (p === '/study/notes' && method === 'POST' && body.id === note.id) {
        const key = String(body.request_id),
          fingerprint = JSON.stringify(body),
          previous = receipts.get(`note:${key}`);
        if (previous) {
          if (previous.fingerprint !== fingerprint)
            fail('NOTE_VERSION_CONFLICT', 'That sample save changed. Reopen the notes.');
          return ok(previous.value);
        }
        if (body.expected_version !== note.version)
          fail('NOTE_VERSION_CONFLICT', 'This sample note changed. Reopen its latest version.');
        note.body = String(body.body ?? '');
        note.title = String(body.title ?? note.title);
        note.status = body.status === 'archived' ? 'archived' : 'active';
        if (body.status === 'deleted') {
          note.available = false;
          notes.splice(notes.indexOf(note), 1);
        }
        note.version++;
        note.updated_at = new Date().toISOString();
        addVersion();
        history.unshift({
          kind: 'note',
          id: `edit-${note.version}`,
          title: `Edited fractions notes · version ${note.version}`,
          at: note.updated_at,
          href: sampleAnalysisPath,
          evidence: 'Your local demo edit · earlier versions retained',
        });
        const value = { id: note.id, version: note.version };
        receipts.set(`note:${key}`, { fingerprint, value });
        return ok(value);
      }
      const notePath = /^\/study\/notes\/([^/]+)(?:\/(.*))?$/.exec(p);
      if (notePath && ownedNote(decodeURIComponent(notePath[1]))) {
        const suffix = notePath[2] ?? '';
        if (!suffix && method === 'GET') return ok(note);
        if (suffix === 'versions' && method === 'GET')
          return ok({ items: versions, has_more: false });
        if (suffix === 'view') {
          if (method === 'POST') {
            if (
              body.expected_view_version !== view.version ||
              body.expected_notebook_version !== note.version
            )
              fail('STUDY_NOTE_VERSION_CHANGED', 'Reopen this sample position.');
            view = {
              ...view,
              version: view.version + 1,
              current_notebook_version: note.version,
              state: body.state as MaterialViewState,
            };
          }
          return ok({ ...view, current_notebook_version: note.version });
        }
        if (suffix === 'knowledge' && method === 'GET')
          return ok({
            items: points.map((point) => {
              const relevant = attempts.filter(
                (attempt) =>
                  examplePractice.find((question) => question.id === attempt.question_id)
                    ?.knowledge_id === point.id,
              );
              return {
                ...point,
                stale: note.version !== 1,
                source_version: 1,
                state: relevant.length
                  ? relevant.at(-1)!.verdict === 'incorrect'
                    ? 'needs_practice'
                    : 'practised'
                  : point.state,
                practice_attempts: relevant.length,
                review_links: relevant.length
                  ? [{ practice_set_id: SAMPLE_IDS.set, question_id: relevant.at(-1)!.question_id }]
                  : [],
              };
            }),
            recommended_id: points[0].id,
            recommendation_reason: 'Source order; start with the whole before calculating.',
            recovery_available: false,
            rebuild_available: false,
            build_state: 'ready',
          });
        const mark = /^knowledge\/([^/]+)\/read$/.exec(suffix);
        if (mark && method === 'POST') {
          const point = points.find((item) => item.id === decodeURIComponent(mark[1]));
          if (!point) fail('EXAMPLE_NOT_FOUND', 'This sample idea is unavailable.');
          if (body.expected_version !== point!.version || note.version !== point!.source_version)
            fail('STUDY_NOTE_VERSION_CHANGED', 'These points keep their earlier source version.');
          point!.state = 'marked_read';
          return ok({ id: point!.id, state: point!.state });
        }
        if (suffix === 'operations' && method === 'GET')
          return ok({
            items: operations.map((operation) => ({
              ...operation,
              stale: operation.source_version !== note.version,
            })),
          });
        const cards = /^operations\/([^/]+)\/cards$/.exec(suffix);
        if (cards && method === 'POST') {
          const item = operations.find(
            (operation) => operation.id === cards[1] && operation.kind === 'flashcards',
          );
          if (!item?.result) fail('EXAMPLE_NOT_FOUND', 'These sample cards are unavailable.');
          const key = `cards:${String(body.request_id)}`,
            fingerprint = JSON.stringify(body),
            previous = receipts.get(key);
          if (previous) {
            if (previous.fingerprint !== fingerprint)
              fail('STUDY_NOTE_VERSION_CHANGED', 'This card request changed.');
            return ok(previous.value);
          }
          if (body.expected_revision !== item!.revision || item!.source_version !== note.version)
            fail('STUDY_NOTE_VERSION_CHANGED', 'Reopen the current sample card revision.');
          if (Array.isArray(body.cards)) {
            if (
              body.cards.length !== item!.result!.cards?.length ||
              body.cards.some(
                (card) =>
                  typeof fields(card).front !== 'string' || typeof fields(card).back !== 'string',
              )
            )
              fail('EXAMPLE_INVALID', 'Keep the sample cards and add your own words.');
            item!.result!.cards = copy(body.cards) as NonNullable<NoteOperation['result']>['cards'];
          } else {
            const review = fields(body.review),
              index = Number(review.index);
            if (
              !Number.isInteger(index) ||
              !item!.result!.cards?.[index] ||
              !['again', 'good'].includes(String(review.rating))
            )
              fail('EXAMPLE_INVALID', 'Choose a sample card and review action.');
            item!.result!.reviews = {
              ...item!.result!.reviews,
              [index]: {
                rating: String(review.rating),
                due_at: new Date(
                  Date.now() + (review.rating === 'good' ? 3 : 1) * 86400000,
                ).toISOString(),
              },
            };
          }
          item!.revision++;
          const value = copy(item!);
          receipts.set(key, { fingerprint, value });
          return ok(value);
        }
        const operation = /^operations\/([^/]+)$/.exec(suffix);
        if (operation && method === 'GET')
          return ok(operations.find((item) => item.id === operation[1]) ?? null);
        if (suffix.startsWith('operations')) return noAi();
      }
      return undefined;
    },
  };
}
